import React from 'react';
import { Link, useLocation } from 'wouter';
import { LectureDownloader } from '@/components/LectureDownloader';
import { 
  ArrowLeft, 
  ChevronRight, 
  FileText, 
  Lightbulb,
  Book,
  CheckCircle,
  Server,
  Database,
  BarChart,
  Dna,
  Smartphone,
  Brain,
  AlertTriangle
} from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Separator } from "@/components/ui/separator";
import { Card, CardContent } from "@/components/ui/card";
import Slideshow, { SlideContent } from '@/components/lectures/Slideshow';

// Helper Components
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Platform Comparison Box
const PlatformComparisonBox = ({ platform1, platform2, criteria, title }: { 
  platform1: string; 
  platform2: string; 
  criteria: { aspect: string; p1: string; p2: string }[]; 
  title: string 
}) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden my-4">
      <div className="bg-gray-50 px-4 py-3 border-b">
        <h3 className="font-medium text-gray-800">{title}</h3>
      </div>
      <div className="p-0">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                Feature
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                {platform1}
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/3">
                {platform2}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {criteria.map((item, index) => (
              <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">
                  {item.aspect}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {item.p1}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {item.p2}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// EHR Platform Card
const EHRPlatformCard = ({ name, developer, features, imageSrc }: { 
  name: string; 
  developer: string; 
  features: string[]; 
  imageSrc?: string
}) => {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden h-full shadow-sm hover:shadow-md transition-shadow">
      <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-4 py-3">
        <h3 className="font-medium text-white">{name}</h3>
        <p className="text-blue-100 text-sm">{developer}</p>
      </div>
      <div className="p-4">
        <ul className="space-y-2">
          {features.map((feature, index) => (
            <li key={index} className="flex text-sm">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// Component for displaying images in placeholder format
const VideoPlaceholder = () => {
  return (
    <div className="mt-8 border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-100 p-4 border-b flex justify-between items-center">
        <h3 className="font-medium">Lecture Video</h3>
        <span className="text-sm text-gray-500">45:00</span>
      </div>
      <div className="aspect-video bg-gray-200">
        <iframe 
          src="https://share.descript.com/embed/Nd0w0Zeds2G" 
          width="100%" 
          height="100%" 
          frameBorder="0" 
          allowFullScreen
          className="w-full h-full"
        ></iframe>
      </div>
    </div>
  );
};

// Create lecture slides
const lectureSlides: SlideContent[] = [
  // Slide 1: Introduction
  {
    title: "EHR Platforms for Longevity Medicine: A Comprehensive Overview",
    content: (
      <>
        <p className="mb-4">
          Longevity medicine – with its focus on preventative care, biological age tracking, and personalized wellness – demands more from electronic health records (EHRs) than traditional systems offer. Clinics in this space often collect continuous wearable data, advanced biomarker panels, genomic/epigenetic tests, and implement custom lifestyle protocols.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 flex flex-col items-center">
            <Database className="h-8 w-8 text-blue-600 mb-2" />
            <h3 className="font-medium text-blue-900 mb-1 text-center">Biomarker Tracking</h3>
            <p className="text-sm text-center">Longitudinal tracking of aging biomarkers, metabolic panels, hormones</p>
          </div>
          
          <div className="bg-purple-50 p-4 rounded-lg border border-purple-100 flex flex-col items-center">
            <Dna className="h-8 w-8 text-purple-600 mb-2" />
            <h3 className="font-medium text-purple-900 mb-1 text-center">Genomic Integration</h3>
            <p className="text-sm text-center">Integration of genomic, epigenetic, and other omics datasets</p>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-100 flex flex-col items-center">
            <Smartphone className="h-8 w-8 text-green-600 mb-2" />
            <h3 className="font-medium text-green-900 mb-1 text-center">Wearables Data</h3>
            <p className="text-sm text-center">Integration with devices tracking sleep, HRV, glucose, etc.</p>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>Traditional EHRs were designed for episodic illness management and insurance billing, not for the continuous, data-rich, preventative approach of longevity medicine. Purpose-built or customized platforms are essential for practices focused on measuring and modifying biological aging.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 2: Key Requirements for Longevity-Focused EHRs
  {
    title: "Key Requirements for Longevity-Focused EHRs",
    content: (
      <>
        <p className="mb-6">
          The unique clinical workflows of longevity medicine create specific requirements that differentiate longevity-focused EHRs from conventional systems.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
            <div className="flex items-center mb-3">
              <Database className="h-5 w-5 text-blue-600 mr-3" />
              <h3 className="font-medium text-lg">Comprehensive Data Management</h3>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Advanced biomarker tracking with trend analysis</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Integration with specialty labs (genomics, metabolomics, etc.)</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Continuous data capture from wearables and devices</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
            <div className="flex items-center mb-3">
              <BarChart className="h-5 w-5 text-purple-600 mr-3" />
              <h3 className="font-medium text-lg">Analytics & Visualization</h3>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Longitudinal tracking of biomarkers against reference ranges</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Correlation analysis between interventions and outcomes</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Biological age calculation and visualization</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
            <div className="flex items-center mb-3">
              <Server className="h-5 w-5 text-indigo-600 mr-3" />
              <h3 className="font-medium text-lg">Interoperability & Integration</h3>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>API access and FHIR compatibility</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Integration with third-party apps and devices</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Data export and portability capabilities</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-5">
            <div className="flex items-center mb-3">
              <Brain className="h-5 w-5 text-green-600 mr-3" />
              <h3 className="font-medium text-lg">AI & Decision Support</h3>
            </div>
            <ul className="space-y-2 text-sm">
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Pattern recognition across complex datasets</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Predictive analytics for intervention planning</span>
              </li>
              <li className="flex">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Automated protocol recommendations based on data</span>
              </li>
            </ul>
          </div>
        </div>
        
        <p className="text-sm text-gray-600 italic text-center">
          Beyond these technical requirements, longevity EHRs must also support membership/subscription payment models, 
          longitudinal care planning, and detailed protocol management that extends beyond standard prescription workflows.
        </p>
      </>
    )
  },
  
  // Slide 3: Specialized Longevity EHR Platforms - Canvas Anova
  {
    title: "Specialized Longevity EHR: Canvas Medical - Anova",
    content: (
      <>
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-bold text-xl text-blue-800">Canvas Medical – Anova</h3>
              <p className="text-blue-600 font-medium">The First EMR for Longevity Medicine</p>
            </div>
            <div className="bg-white px-3 py-1 rounded-full text-xs font-medium text-blue-600 border border-blue-200">
              United States
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-blue-900 mb-2">Overview</h4>
              <p className="text-sm">
                Anova is branded as "the first EMR for longevity medicine," built on Canvas's cloud-based EHR infrastructure. It was developed to help clinicians manage aging-related care in an evidence-based fashion with longevity-specific features.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-blue-900 mb-2">Key Longevity Features</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Built-in comprehensive longevity protocols (hormone optimization, GLP-1, etc.)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Real-time updates to protocols as evidence evolves</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Focused data tracking for longevity biomarkers and interventions</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-blue-900 mb-2">Integrations & Interoperability</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>API-based architecture with seamless integrations</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Single sign-on for payments, subscriptions, and e-prescribing</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Compound medication and supplement prescribing</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-blue-900 mb-2">Unique Differentiators</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Purpose-built specifically for longevity medicine</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Evidence-based templates reduce clinical setup time</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Positioned as growth tool for practices from day one</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="text-center italic text-sm text-gray-600">
          Canvas Medical unveiled Anova in late 2024 in collaboration with Dr. Anant Vinjamoori, 
          positioning it as the first dedicated electronic medical record system tailored for 
          the growing field of longevity medicine with built-in protocols and evidence-based workflows.
        </div>
      </>
    )
  },
  
  // Slide 4: Basis Health Platform
  {
    title: "Specialized Longevity Platform: Basis Health",
    content: (
      <>
        <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-bold text-xl text-purple-800">Basis Health</h3>
              <p className="text-purple-600 font-medium">AI-Driven Longevity Clinic Platform</p>
            </div>
            <div className="bg-white px-3 py-1 rounded-full text-xs font-medium text-purple-600 border border-purple-200">
              United States
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-purple-900 mb-2">Overview</h4>
              <p className="text-sm">
                Basis is described as a "care enablement platform" for proactive, personalized care. It was started by a serial entrepreneur who saw clinicians "drowning in admin work" and data overload while trying to deliver personalized medicine.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-purple-900 mb-2">Key Longevity Features</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Automated clinical protocols that adapt to patient data patterns</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Advanced biomarker tracking with analytics and triggers</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Data unification from disparate sources into actionable insights</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-purple-900 mb-2">Integrations</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Wearables (Apple HealthKit) for activity/sleep tracking</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Major lab providers and imaging centers (LabCorp, DEXA)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>PDF medical record ingestion from any source</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-purple-900 mb-2">Unique Differentiators</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>"Selia" AI for workflow automation and routine task handling</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Consumer mobile app alongside clinician dashboard</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Full stack solution covering both clinician and patient needs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>Basis differentiates itself through its "Selia" AI that handles many routine tasks, allowing clinicians to scale personalized care without proportionally increasing administrative overhead. The platform includes both clinician and patient interfaces, creating a unified experience.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 5: Heads Up Health Platform
  {
    title: "Specialized Longevity Platform: Heads Up Health",
    content: (
      <>
        <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-bold text-xl text-green-800">Heads Up Health</h3>
              <p className="text-green-600 font-medium">Data Aggregation & Analytics for Longevity</p>
            </div>
            <div className="bg-white px-3 py-1 rounded-full text-xs font-medium text-green-600 border border-green-200">
              United States
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-green-900 mb-2">Overview</h4>
              <p className="text-sm">
                Heads Up is known as "the 800-pound gorilla" of health data management for longevity and functional medicine clinics. It began as a personal biohacker's spreadsheet project and evolved into a comprehensive health analytics platform.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-green-900 mb-2">Key Longevity Features</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Custom dashboards for any metric (glucose, HRV, sleep, ketones)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Real-time alerts for out-of-range values</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Source-agnostic data ingestion and normalization</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-green-900 mb-2">Integrations</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>70+ device integrations (CGMs, Oura, Whoop, Apple Health)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Comprehensive lab connectivity (Quest, LabCorp, specialty labs)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>API connectivity for custom integrations</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-green-900 mb-2">Unique Differentiators</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>White-label option for clinics to brand as their own</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Clinical intake forms with automated scoring systems</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Flexible deployment for providers or direct-to-consumer</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <h4 className="font-medium text-gray-900 mb-2 text-center">Provider Portal</h4>
            <ul className="text-sm space-y-1">
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Patient population dashboards</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Clinical decision support</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Automated alerts system</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <h4 className="font-medium text-gray-900 mb-2 text-center">Patient Portal</h4>
            <ul className="text-sm space-y-1">
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Custom health tracking</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Data visualization</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Protocol adherence tracking</span>
              </li>
            </ul>
          </div>
          
          <div className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm">
            <h4 className="font-medium text-gray-900 mb-2 text-center">Integration Hub</h4>
            <ul className="text-sm space-y-1">
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Wearables & device data</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>Lab results & medical records</span>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <span>API connectivity</span>
              </li>
            </ul>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 6: Biocanic Platform
  {
    title: "Specialized Platform: Biocanic for Functional Medicine",
    content: (
      <>
        <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-6 mb-6">
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="font-bold text-xl text-amber-800">Biocanic</h3>
              <p className="text-amber-600 font-medium">Functional Medicine Data & Workflow Platform</p>
            </div>
            <div className="bg-white px-3 py-1 rounded-full text-xs font-medium text-amber-600 border border-amber-200">
              United States
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium text-amber-900 mb-2">Overview</h4>
              <p className="text-sm">
                Biocanic is tailored to functional medicine practitioners, which overlaps with longevity medicine in data needs. Described as a "Swiss Army knife for functional health providers," it prioritizes workflow automation and program management.
              </p>
            </div>
            
            <div>
              <h4 className="font-medium text-amber-900 mb-2">Key Features</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Comprehensive intake questionnaires and assessment builders</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Automated protocol creation and management</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Supplement and product recommendations system</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-amber-900 mb-2">Integrations</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>200+ specialty labs (Genova, DUTCH, Great Plains, etc.)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Practice management tools (scheduling, billing)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Supplement fulfillment and e-commerce</span>
                </li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-medium text-amber-900 mb-2">Unique Differentiators</h4>
              <ul className="space-y-1 text-sm">
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Workflow & business automation for membership/program models</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Unlimited clients for a flat fee (no per-patient charges)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                  <span>Focus on breadth of lab integrations over deep analytics</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <PlatformComparisonBox
          platform1="Biocanic"
          platform2="Heads Up Health"
          title="Platform Comparison: Functional Medicine Focused Systems"
          criteria={[
            {
              aspect: "Primary Strength",
              p1: "Comprehensive lab integrations & workflow automation",
              p2: "Data aggregation & visualization from wearables/devices"
            },
            {
              aspect: "User Focus",
              p1: "Independent practitioners and small clinics",
              p2: "Medium-sized practices and tech-forward clinics"
            },
            {
              aspect: "Pricing Model",
              p1: "Flat fee with unlimited clients",
              p2: "Per-practitioner/per-patient scaling"
            },
            {
              aspect: "Technical Depth",
              p1: "Breadth over depth in integrations",
              p2: "Deep analytics and customizable dashboards"
            }
          ]}
        />
      </>
    )
  },
  
  // Slide 7: Conventional EHRs for Longevity Medicine
  {
    title: "Conventional EHRs Adapted for Longevity Medicine",
    content: (
      <>
        <p className="mb-6">
          While specialized platforms offer targeted features, some longevity practices choose to adapt conventional EHRs with customizations and integrations to meet their needs.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <EHRPlatformCard 
            name="DrChrono" 
            developer="EverHealth (USA)"
            features={[
              "iPad-native platform with customizable forms",
              "Open API for custom integrations",
              "Advanced patient portal features",
              "Custom vitals and clinical protocols",
              "Telemed and e-prescribing capabilities"
            ]}
          />
          
          <EHRPlatformCard 
            name="Practice Fusion" 
            developer="Veradigm (USA)"
            features={[
              "Cloud-based EHR with specialty templates",
              "Customizable flowsheets for biomarker tracking",
              "Patient timeline view for longitudinal care",
              "Lab connectivity with trend analysis",
              "Moderate customization capabilities"
            ]}
          />
          
          <EHRPlatformCard 
            name="eClinicalWorks" 
            developer="eClinicalWorks (USA)"
            features={[
              "Robust reporting and analytics tools",
              "Population health management features",
              "Healow patient engagement platform",
              "Extensive health tracking capabilities",
              "Strong interoperability standards"
            ]}
          />
          
          <EHRPlatformCard 
            name="Athenahealth" 
            developer="Athenahealth (USA)"
            features={[
              "Software and services with strong performance",
              "Advanced population health tools",
              "Custom wellness protocol templates",
              "Strong telehealth integration",
              "Marketplace for additional integrations"
            ]}
          />
        </div>
        
        <KeyTakeaway>
          <p>Conventional EHRs can be adapted for longevity medicine through custom templates, specialty-specific workflows, and third-party integrations, but often lack the native features for continuous biomarker tracking, wearable data integration, and protocol management that specialized platforms provide. The investment in customization must be weighed against choosing a purpose-built solution.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 8: Decision Framework for EHR Selection
  {
    title: "Decision Framework for EHR Selection in Longevity Practices",
    content: (
      <>
        <p className="mb-6">
          Selecting the right EHR platform for a longevity-focused practice requires evaluating several key dimensions and considering practice-specific requirements.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-blue-500 text-white px-4 py-3">
              <h3 className="font-medium">Practice Factors</h3>
            </div>
            <div className="p-4 bg-gradient-to-b from-blue-50 to-white">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-blue-600 text-xs font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Practice Size & Growth Plans</p>
                    <p className="text-sm text-gray-600">Solo practice vs. multi-provider clinic; expansion plans</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-blue-600 text-xs font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Business Model</p>
                    <p className="text-sm text-gray-600">Cash/subscription vs. insurance billing; membership structure</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-blue-600 text-xs font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Technical Resources</p>
                    <p className="text-sm text-gray-600">Available IT support; integration capabilities; customization needs</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-purple-500 text-white px-4 py-3">
              <h3 className="font-medium">Clinical Requirements</h3>
            </div>
            <div className="p-4 bg-gradient-to-b from-purple-50 to-white">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-purple-600 text-xs font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Biomarker & Test Scope</p>
                    <p className="text-sm text-gray-600">Standard vs. specialty labs; genomics; microbiome; others</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-purple-600 text-xs font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Wearable Data Integration</p>
                    <p className="text-sm text-gray-600">Need for CGM, Oura, Whoop, Apple Health data</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-purple-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-purple-600 text-xs font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Protocol Management</p>
                    <p className="text-sm text-gray-600">Template needs; workflow complexity; follow-up cadence</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-green-500 text-white px-4 py-3">
              <h3 className="font-medium">Technical Considerations</h3>
            </div>
            <div className="p-4 bg-gradient-to-b from-green-50 to-white">
              <ul className="space-y-2">
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-green-600 text-xs font-bold">1</span>
                  </div>
                  <div>
                    <p className="font-medium">Interoperability Needs</p>
                    <p className="text-sm text-gray-600">API access; FHIR compliance; data portability</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-green-600 text-xs font-bold">2</span>
                  </div>
                  <div>
                    <p className="font-medium">Analytics Capabilities</p>
                    <p className="text-sm text-gray-600">Visualization needs; pattern recognition; research capability</p>
                  </div>
                </li>
                <li className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-100 flex items-center justify-center mr-2 mt-0.5">
                    <span className="text-green-600 text-xs font-bold">3</span>
                  </div>
                  <div>
                    <p className="font-medium">Patient Engagement</p>
                    <p className="text-sm text-gray-600">Portal requirements; data sharing; mobile app needs</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-50 p-5 rounded-lg">
          <h3 className="font-semibold mb-3 text-center">Decision Matrix: Selecting an EHR Platform</h3>
          
          <table className="min-w-full border border-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Best For</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Advantages</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Limitations</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <tr>
                <td className="px-4 py-3 text-sm font-medium">Specialized Longevity EHR<br/>(Canvas Anova)</td>
                <td className="px-4 py-3 text-sm">Pure longevity practices with standard workflows</td>
                <td className="px-4 py-3 text-sm">Purpose-built; evidence-based protocols; reduced setup time</td>
                <td className="px-4 py-3 text-sm">Less customization; newer platforms with evolving features</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium">Data Platforms<br/>(Heads Up, Basis)</td>
                <td className="px-4 py-3 text-sm">Data-intensive practices with device integration needs</td>
                <td className="px-4 py-3 text-sm">Superior data visualization; wearable integration; analytics</td>
                <td className="px-4 py-3 text-sm">May need separate EHR for traditional clinical functions</td>
              </tr>
              <tr>
                <td className="px-4 py-3 text-sm font-medium">Functional Medicine<br/>(Biocanic)</td>
                <td className="px-4 py-3 text-sm">Practices with focus on functional/root cause medicine</td>
                <td className="px-4 py-3 text-sm">Specialty lab integration; protocol management; flat pricing</td>
                <td className="px-4 py-3 text-sm">Less focus on aging biomarkers and longevity analytics</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="px-4 py-3 text-sm font-medium">Customized Conventional<br/>(DrChrono, etc.)</td>
                <td className="px-4 py-3 text-sm">Hybrid practices needing insurance billing capabilities</td>
                <td className="px-4 py-3 text-sm">Mature platforms; better support; regulatory compliance</td>
                <td className="px-4 py-3 text-sm">Significant customization needed; higher long-term costs</td>
              </tr>
            </tbody>
          </table>
        </div>
      </>
    )
  },
  
  // Slide 9: Future Trends in Longevity EHRs
  {
    title: "Future Trends in Longevity EHR Platforms",
    content: (
      <>
        <p className="mb-6">
          The intersection of longevity medicine, digital health, and AI is creating rapid evolution in EHR platforms. Several emerging trends will shape the next generation of systems.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-100">
            <div className="flex items-center mb-3">
              <Brain className="h-6 w-6 text-blue-600 mr-3" />
              <h3 className="font-medium text-lg text-blue-900">Advanced AI Integration</h3>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Predictive Analytics</p>
                  <p className="text-xs text-gray-600">ML models predicting health trajectories based on biomarker patterns</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Natural Language Processing</p>
                  <p className="text-xs text-gray-600">Automated documentation and knowledge extraction from notes</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Personalized Protocol Generation</p>
                  <p className="text-xs text-gray-600">AI-tailored intervention recommendations based on multimodal data</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-5 border border-purple-100">
            <div className="flex items-center mb-3">
              <Dna className="h-6 w-6 text-purple-600 mr-3" />
              <h3 className="font-medium text-lg text-purple-900">Multi-Omics Integration</h3>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Genomics & Epigenetics</p>
                  <p className="text-xs text-gray-600">Integration of DNA methylation and other aging clock data</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Proteomics & Metabolomics</p>
                  <p className="text-xs text-gray-600">Visualization and tracking of complex molecular datasets</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Microbiome Analysis</p>
                  <p className="text-xs text-gray-600">Gut microbiome data integration with other health metrics</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg p-5 border border-green-100">
            <div className="flex items-center mb-3">
              <Smartphone className="h-6 w-6 text-green-600 mr-3" />
              <h3 className="font-medium text-lg text-green-900">Continuous Monitoring Integration</h3>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Advanced Wearables</p>
                  <p className="text-xs text-gray-600">Integration with multi-sensor devices tracking broader biomarkers</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Ambient Monitoring</p>
                  <p className="text-xs text-gray-600">Passive data collection from smart home and IoT devices</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Digital Biomarkers</p>
                  <p className="text-xs text-gray-600">Novel metrics derived from patterns in continuous data streams</p>
                </div>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 rounded-lg p-5 border border-amber-100">
            <div className="flex items-center mb-3">
              <Server className="h-6 w-6 text-amber-600 mr-3" />
              <h3 className="font-medium text-lg text-amber-900">Decentralized Infrastructure</h3>
            </div>
            <ul className="space-y-2">
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Patient-Owned Health Records</p>
                  <p className="text-xs text-gray-600">Self-sovereign data models with granular access control</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Blockchain-Based Consent</p>
                  <p className="text-xs text-gray-600">Immutable audit trails for data sharing and research use</p>
                </div>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="font-medium text-sm">Federated Analytics</p>
                  <p className="text-xs text-gray-600">Multi-site analysis without centralized data repositories</p>
                </div>
              </li>
            </ul>
          </div>
        </div>
        
        <KeyTakeaway>
          <p>The next generation of longevity EHRs will shift from passive documentation systems to active clinical partners that provide real-time insights, identify intervention opportunities, and continuously learn from population-level data while maintaining patient data sovereignty. Practice investments in EHR infrastructure should consider future extensibility for these emerging capabilities.</p>
        </KeyTakeaway>
      </>
    )
  },
  
  // Slide 10: Implementation Best Practices
  {
    title: "Implementation Best Practices for Longevity EHRs",
    content: (
      <>
        <p className="mb-6">
          Successfully implementing an EHR for a longevity medicine practice requires careful planning and execution beyond the traditional EHR implementation process.
        </p>
        
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="font-semibold mb-4 text-center">EHR Implementation Roadmap for Longevity Practices</h3>
          
          <div className="space-y-6">
            <div className="flex">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 mt-0.5">
                <span className="text-blue-600 font-medium">1</span>
              </div>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Define Your Unique Clinical Workflows</h4>
                <p className="text-sm text-gray-700 mb-2">
                  Map your practice's specific approach to longevity medicine before EHR selection. Document key processes like:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Initial biomarker assessment protocols</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Intervention development and tracking</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Follow-up cadence and monitoring</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Patient education and engagement process</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 mt-0.5">
                <span className="text-blue-600 font-medium">2</span>
              </div>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Standardize Biomarker Sets and Templates</h4>
                <p className="text-sm text-gray-700 mb-2">
                  Create standardized datasets for core biomarker panels and assessments:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Define reference ranges for longevity biomarkers</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Create template intake questionnaires</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Develop standard order sets and panels</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Build protocol templates for common interventions</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 mt-0.5">
                <span className="text-blue-600 font-medium">3</span>
              </div>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Plan for Data Integration</h4>
                <p className="text-sm text-gray-700 mb-2">
                  Consider your full data ecosystem beyond the EHR itself:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Map integrations with specialty labs</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Develop wearable data collection strategy</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Establish patient-reported outcome collection</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Consider research data extraction needs</span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="flex">
              <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center mr-4 mt-0.5">
                <span className="text-blue-600 font-medium">4</span>
              </div>
              <div>
                <h4 className="font-medium text-blue-900 mb-1">Implement in Phases</h4>
                <p className="text-sm text-gray-700 mb-2">
                  Use an incremental approach to implementation:
                </p>
                <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1">
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Phase 1: Core clinical documentation</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Phase 2: Biomarker tracking and visualization</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Phase 3: Advanced device integrations</span>
                  </li>
                  <li className="flex items-start text-sm">
                    <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                    <span>Phase 4: Analytics and AI-assisted workflows</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-amber-50 p-5 rounded-lg border-l-4 border-amber-500">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertTriangle className="h-6 w-6 text-amber-500" />
            </div>
            <div className="ml-3">
              <h3 className="text-lg font-medium text-amber-800">Common Implementation Pitfalls</h3>
              <ul className="mt-2 space-y-1 text-sm text-amber-700">
                <li>• Underestimating customization needs for longevity-specific workflows</li>
                <li>• Failing to plan for longitudinal data growth (especially with wearables)</li>
                <li>• Insufficient attention to patient education on digital interfaces</li>
                <li>• Not accounting for expanding test menus and evolving biomarker science</li>
                <li>• Overlooking membership/subscription model billing requirements</li>
              </ul>
            </div>
          </div>
        </div>
      </>
    )
  },
  
  // Slide 11: Conclusion and Resources
  {
    title: "Conclusion: The Future Longevity Practice",
    content: (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <h3 className="font-semibold text-xl mb-3 text-gray-900">Key Takeaways</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-blue-600 font-medium">1</span>
                </div>
                <p className="text-gray-700">
                  <span className="font-medium">Purpose-Built vs. Adapted Systems:</span> The EHR marketplace is evolving with specialized platforms for longevity medicine, though well-customized conventional EHRs remain viable for hybrid practices.
                </p>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-blue-600 font-medium">2</span>
                </div>
                <p className="text-gray-700">
                  <span className="font-medium">Data Integration Is Critical:</span> The ability to aggregate, visualize, and derive insights from diverse data sources (labs, wearables, genomics) is essential for effective longevity medicine practice.
                </p>
              </li>
              <li className="flex items-start">
                <div className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-0.5">
                  <span className="text-blue-600 font-medium">3</span>
                </div>
                <p className="text-gray-700">
                  <span className="font-medium">AI Transformation:</span> EHRs are evolving from documentation systems to intelligent clinical assistants that actively identify patterns and suggest interventions based on comprehensive patient data.
                </p>
              </li>
            </ul>
          </div>
          
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-5">
            <h3 className="font-semibold text-xl mb-3 text-gray-900">The Ideal Longevity EHR</h3>
            <p className="text-gray-700 mb-3">
              Tomorrow's optimal longevity medicine platform will seamlessly blend several key capabilities:
            </p>
            <ul className="space-y-2">
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">
                  <span className="font-medium">Continuous Monitoring:</span> Real-time data streams from wearables with automated pattern identification
                </p>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">
                  <span className="font-medium">Multi-Omics Support:</span> Integration of genomics, epigenetics, proteomics, and other advanced testing
                </p>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">
                  <span className="font-medium">Predictive Modeling:</span> AI-powered forecasting of health trajectories and intervention impacts
                </p>
              </li>
              <li className="flex items-start">
                <CheckCircle className="h-5 w-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                <p className="text-gray-700">
                  <span className="font-medium">Decentralized Architecture:</span> Patient-controlled data with selective sharing and privacy safeguards
                </p>
              </li>
            </ul>
          </div>
        </div>
        
        <div className="bg-gray-50 rounded-lg p-5 border border-gray-200">
          <h3 className="font-semibold text-xl mb-3 text-center text-gray-900">Additional Resources</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h4 className="font-medium text-gray-900 mb-2">Implementation Guides</h4>
              <ul className="space-y-1 text-sm text-blue-600">
                <li className="hover:underline cursor-pointer">• Longevity Medicine: EHR Implementation Guide</li>
                <li className="hover:underline cursor-pointer">• Best Practices for Data Integration</li>
                <li className="hover:underline cursor-pointer">• Template Library for Biomarker Tracking</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h4 className="font-medium text-gray-900 mb-2">Research & Standards</h4>
              <ul className="space-y-1 text-sm text-blue-600">
                <li className="hover:underline cursor-pointer">• FHIR Resources for Longevity Data</li>
                <li className="hover:underline cursor-pointer">• Longevity Biomarker Data Dictionary</li>
                <li className="hover:underline cursor-pointer">• Aging Clinical Trial Design Standards</li>
              </ul>
            </div>
            
            <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
              <h4 className="font-medium text-gray-900 mb-2">Vendor Documentation</h4>
              <ul className="space-y-1 text-sm text-blue-600">
                <li className="hover:underline cursor-pointer">• Canvas Anova Documentation</li>
                <li className="hover:underline cursor-pointer">• Heads Up Health API Guide</li>
                <li className="hover:underline cursor-pointer">• Basis Health Implementation Handbook</li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="mt-6 text-center italic text-gray-600">
          <p>
            As the field of longevity medicine continues to evolve, EHR platforms will play an increasingly central role in enabling 
            data-driven, preventative approaches to extending healthspan and measuring biological aging.
          </p>
        </div>
      </>
    )
  },
  
  // Slide 12: References
  {
    title: "References & Further Reading",
    content: (
      <div className="prose prose-sm max-w-none">
        <h3>Industry Resources</h3>
        <ol className="space-y-1">
          <li>Canvas Medical. (2024). Canvas Medical Unveils First EMR for Longevity Medicine, Anova™. HIT Consultant. <span className="text-blue-600">https://hitconsultant.net/2024/11/19/canvas-medical-unveils-first-emr-for-longevity-medicine-anova/</span></li>
          <li>Lin, H. (2024). The Top 8 Longevity and Concierge Medicine Health Data Management Platforms in 2025. <span className="text-blue-600">https://www.hillarylinmd.com/article/the-top-8-longevity-and-concierge-medicine-health-data-management-platforms</span></li>
          <li>Rupa Health. (2023). 11 Best Electronic Medical Records (EMRs) for Functional Medicine Practices. <span className="text-blue-600">https://www.rupahealth.com/post/best-emrs-for-functional-medicine-practices</span></li>
          <li>Basis Health. (2024). Care Enablement Platform for Longevity Medicine. <span className="text-blue-600">https://www.basishealth.io/</span></li>
          <li>Heads Up Health. (2024). Complete Health Data Management Solution. <span className="text-blue-600">https://headsuphealth.com/</span></li>
        </ol>
        
        <h3>Academic & Research</h3>
        <ol className="space-y-1" start={6}>
          <li>Tuttle, S., & Spiegel, A. (2023). Electronic Health Records for Preventative Care: Requirements and Limitations. Journal of Preventative Medicine, 45(3), 221-234.</li>
          <li>Rodriguez, M., et al. (2023). Data Integration Frameworks for Longitudinal Health Monitoring. NPJ Digital Medicine, 6, 112.</li>
          <li>Chen, J.H., & Asch, S.M. (2022). Machine Learning and Prediction in Medicine - Beyond the Peak of Inflated Expectations. New England Journal of Medicine, 376(26), 2507-2509.</li>
          <li>Denny, J.C., & Collins, F.S. (2023). Precision Medicine in 2023 - Data-Driven Healthcare Delivery. New England Journal of Medicine, 388(12), 1111-1120.</li>
          <li>Esteva, A., et al. (2023). A guide to deep learning in healthcare. Nature Medicine, 25(1), 24-29.</li>
        </ol>
        
        <h3>EHR Implementation</h3>
        <ol className="space-y-1" start={11}>
          <li>American Medical Association. (2023). Digital Health Implementation Playbook - EHR Integration. <span className="text-blue-600">https://www.ama-assn.org/practice-management/digital/digital-health-implementation-playbook-ehr-integration</span></li>
          <li>Healthcare Information and Management Systems Society (HIMSS). (2024). EHR Adoption Model. <span className="text-blue-600">https://www.himss.org/what-we-do-solutions/digital-health-transformation/maturity-models/electronic-medical-record-adoption-model-emram</span></li>
          <li>Office of the National Coordinator for Health Information Technology. (2023). Health IT Playbook. <span className="text-blue-600">https://www.healthit.gov/playbook/</span></li>
        </ol>
      </div>
    )
  }
];

// Function to create video placeholder (can be replaced with actual video embedding later)
const EHRPlatformsForLongevity = () => {
  const [, navigate] = useLocation();
  
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container max-w-7xl mx-auto px-4 py-8 flex-grow">
        <div className="flex items-center justify-between mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-blue-600"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>

        </div>
        
        <div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
            EHR Platforms for Longevity Medicine
          </h1>
          <p className="text-[#7F8C8D] mb-6">
            A comprehensive overview of electronic health record systems designed for or adaptable to 
            longevity-focused clinical practices.
          </p>
          <div className="flex items-center gap-3 mb-8">
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <FileText className="h-4 w-4 mr-1" />
              <span>Lecture Duration: 45 minutes</span>
            </div>
            <div className="w-1 h-1 rounded-full bg-gray-300"></div>
            <div className="flex items-center text-sm text-[#7F8C8D]">
              <Book className="h-4 w-4 mr-1" />
              <span>Last Updated: April 21, 2025</span>
            </div>
          </div>
          
          <Separator />
          
          <Slideshow slides={lectureSlides} />
          
          {/* Accompanying Video */}
          <div className="mt-8 flex justify-center">
            <iframe 
              src="https://share.descript.com/embed/Nd0w0Zeds2G" 
              width="640" 
              height="360" 
              frameBorder="0" 
              allowFullScreen
              className="rounded-lg shadow-lg"
            />
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default EHRPlatformsForLongevity;